import { Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, signal, SimpleChanges } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Metaproduct, MetaproductComponent, MetaproductOption } from '@scalefast/core';
import { AnalyticsStoreService } from '../../../shared/services';
import { NgClass } from '@angular/common';
import { AlphabeticalSortPipe } from 'src/app/shared/pipes/alphabetical-sort.pipe';

@Component({
  selector: 'app-color-metaproduct',
  styleUrls: ['./color-metaproduct.styles.scss'],
  templateUrl: './color-metaproduct.component.html',
  standalone: true,
  imports: [NgClass, RouterModule, AlphabeticalSortPipe],
})
export class AppColorMetaproductComponent implements OnInit, OnChanges, MetaproductComponent {
  @Input() metaproducts!: Metaproduct[];
  @Input() colorReferences: Record<string, string> | undefined = {};
  @Input() isProductPage = true;
  @Input() showColorNameTitle = true;
  @Input() showDropdown = false;
  @Output() colorChanged = new EventEmitter<string>(false);
  @Input() productId: string | undefined;
  @Input() parentId: string | undefined;
  @Input() customClass: string = '';
  @Input() inputColor: string = '';

  _zone: NgZone;
  _router: Router;
  dropdownOpen = signal(false);

  selectedColor: string = '';
  colorMetaproducts: Metaproduct = {
    type: 'color',
    options: [],
  };

  constructor(
    private router: Router,
    private zone: NgZone,
    private analyticsStoreService: AnalyticsStoreService,
  ) {
    this._zone = zone;
    this._router = router;
  }

  ngOnInit(): void {
    this.colorMetaproducts = this.metaproducts[0];
    this.setMetaproducts();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.metaproducts) {
      this.colorMetaproducts = changes.metaproducts.currentValue[0];
      this.setMetaproducts();
    }
    if (changes['productId']) {
      this.setSelectedColor();
    }
  }

  toggleDropdown() {
    this.dropdownOpen.update((isOpen) => !isOpen);
  }

  translatetoHexcode(options: MetaproductOption): string {
    if (options.hexcode && options.hexcode !== '') {
      return options.hexcode;
    }

    const colorName = options?.color ?? '';
    if (!this.colorReferences?.[colorName]) {
      return '';
    }

    return this.colorReferences[colorName];
  }

  redirectToColorSelected(productId: string, color: string, index: number) {
    if (this.isProductPage) {
      this._zone.run(() => {
        this._router.navigate(['/product', productId]);
      });
    } else {
      // Product list case
      this.selectedColor = color;
      this.colorChanged.emit(productId);
    }
    this.dropdownOpen.update((isOpen) => !isOpen);
  }

  setMetaproducts(): void {
    if (this.colorMetaproducts?.options) {
      const { options } = this.colorMetaproducts;
      if (options.length > 1) {
        if (this.productId) {
          this.setSelectedColor();
        }
        options.sort((a, b) => (a.color?.toLowerCase() ?? '').localeCompare(b.color?.toLowerCase() ?? ''));
      } else {
        this.selectedColor = options[0]?.color ?? '';
      }
    }
  }

  setSelectedColor() {
    if (this.colorMetaproducts?.options) {
      if (this.isProductPage) {
        this.selectedColor = this.colorMetaproducts?.parentProduct?.color ?? '';
      } else {
        const { options } = this.colorMetaproducts;
        const matchingOption = options.find((option) => option.id === this.productId);
        if (matchingOption) {
          this.selectedColor = matchingOption.color ?? '';
        } else if (this.inputColor.length > 0) {
          this.selectedColor = this.inputColor;
        }
      }
    }
  }

  gtmEvent(event: any): void {
    const category = event.currentTarget.getAttribute('data-gtmcategory');
    const action = event.currentTarget.getAttribute('data-gtmaction');
    const label = event.currentTarget.getAttribute('data-gtmlabel');
    this.analyticsStoreService.miscEvent(category, action, label);
  }
}
